import React, { ReactElement, useContext } from 'react'
import { TextTypographyProps, Typography } from '@grammarly/badger-ui'

import { useFeatureHighlightContext } from '@contexts/feature-highlight-context'
import { SectionContext } from '@contexts/section-context'
import { useIsSectionThemeDark } from '@contexts/section-theme-context'
import { DescriptionSize, EyebrowStyle, IconSpacer, TitleSpacer, TypeItemFeatureHighlight } from '@lib/types'
import { ItemFeatureHighlightDecoration } from '@lib/types/item-feature-highlight-decoration'
import { useBrowserService } from '@lib/hooks'
import { AssetRenderer } from '@components/renderer/asset'
import { FixedSpacer, SpacerMultiplier } from '@ui-lib/spacer'

import { ContentProps } from '../../renderer'
import { renderRichTextMultiLineContent, renderRichTextSingleLineContent } from '../../renderer/rich-text'

import styles from './feature-highlight.module.scss'

interface FeatureHighlightProps extends ContentProps<TypeItemFeatureHighlight> {
  titleTag?: 'h1' | 'h2'
  isToolsRedesign2_XEnabled?: boolean
}

const descriptionTypo: Record<DescriptionSize, (props: TextTypographyProps) => ReactElement> = {
  [DescriptionSize.Large]: Typography.TXL,
  [DescriptionSize.Medium]: Typography.C1,
}
const eyebrowTypo: Record<DescriptionSize, (props: TextTypographyProps) => ReactElement> = {
  [DescriptionSize.Large]: Typography.TXL,
  [DescriptionSize.Medium]: Typography.C1,
}

const titleSpacerToMultiplier: Record<TitleSpacer, SpacerMultiplier> = {
  [TitleSpacer.None]: 0,
}

export const FeatureHighlight: React.FC<FeatureHighlightProps> = (props) => {
  const {
    structure: {
      fields: {
        title,
        description,
        eyebrowTitle,
        iconAsset,
        iconSpacer,
        titleSize: titleSizeOverride,
        eyebrowStyle,
        decoration,
        descriptionSize,
        internalName,
        titleSpacer,
      },
    },
    titleTag,
    isToolsRedesign2_XEnabled,
  } = props

  const { titleStyle: titleStyleFromContext } = useFeatureHighlightContext()
  const titleStyle = titleSizeOverride || titleStyleFromContext
  const isLargeTitle = titleStyle === 'T1' || titleStyle === 'HXL' || titleStyle === 'HL'
  const isLargeEyebrow = eyebrowStyle === EyebrowStyle.Large
  const isLargeIconSpacer = iconSpacer === IconSpacer.Large
  const TitleComponent = Typography[titleStyle]
  const EyebrowComponent = eyebrowTypo[eyebrowStyle ?? EyebrowStyle.Medium]
  const DescriptionComponent = descriptionTypo[descriptionSize ?? DescriptionSize.Medium]
  const { hasGroupHeading } = useContext(SectionContext)
  const isDarkTheme = useIsSectionThemeDark()
  const { isMobile } = useBrowserService()

  const iconSpacerMultiplier = isLargeIconSpacer ? (isMobile ? 3 : 5) : 2
  const eyebrowTitleSpacerMultiplier = isLargeEyebrow ? (isMobile ? 3 : 5) : 1

  const defaultTitleSpacerMultiplier = isLargeTitle ? (isMobile ? 3 : 5) : 2
  const titleSpacerMultiplier = isToolsRedesign2_XEnabled
    ? 3
    : titleSpacer && titleSpacerToMultiplier[titleSpacer] !== undefined
      ? titleSpacerToMultiplier[titleSpacer]
      : defaultTitleSpacerMultiplier

  const descriptionSpacerMultiplier = isToolsRedesign2_XEnabled ? 1 : isLargeTitle ? 5 : 1

  return (
    <div className={styles.wrapperStyle} data-component-id={internalName}>
      {iconAsset && (
        <>
          <AssetRenderer structure={iconAsset} />
          <FixedSpacer multiplier={iconSpacerMultiplier} />
        </>
      )}
      {eyebrowTitle && (
        <>
          {isLargeEyebrow ? (
            <EyebrowComponent as="div">{renderRichTextMultiLineContent(eyebrowTitle)}</EyebrowComponent>
          ) : (
            <Typography.C5 className={isDarkTheme ? styles.eyebrowTextDarkMode : styles.eyebrowTextLightMode} as="div">
              {renderRichTextSingleLineContent(eyebrowTitle)}
            </Typography.C5>
          )}
          <FixedSpacer multiplier={eyebrowTitleSpacerMultiplier} />
        </>
      )}
      {title && (
        <>
          <TitleComponent as={hasGroupHeading ? 'h3' : (titleTag ?? 'h2')}>
            {renderRichTextMultiLineContent(title)}
          </TitleComponent>
          <FixedSpacer multiplier={titleSpacerMultiplier} />
        </>
      )}
      {description && (
        <>
          <DescriptionComponent
            as="div"
            className={
              decoration === ItemFeatureHighlightDecoration.BubbleN0
                ? isMobile
                  ? styles.bubbleN0Mobile
                  : styles.bubbleN0Desktop
                : undefined
            }
          >
            {renderRichTextMultiLineContent(description)}
          </DescriptionComponent>
          <FixedSpacer multiplier={descriptionSpacerMultiplier} />
        </>
      )}
    </div>
  )
}
